import type {
  MillFloorLoad,
  MillFloorQueueLot,
  MillFloorUsinage,
} from "@/features/mill-floor/api/types"

// Bags that actually reached the warehouse: bagsUnloaded minus what
// sondage rejected (wet + other). Rejected bags never make it past
// reception so they must not be visible to milling.
//
// `sampling.acceptedBags` is authoritative when present; we fall back
// to recomputing from parts (legacy rows), and finally to bagsUnloaded
// for loads that haven't gone through sondage yet (still in QUALITY
// status, never displayed in mill-floor anyway).
export function loadTotalBags(load: MillFloorLoad): number {
  const s = load.sampling
  if (s) {
    if (typeof s.acceptedBags === "number" && s.acceptedBags > 0) {
      return s.acceptedBags
    }
    const wet = s.bagsWet ?? 0
    const other = s.otherRejected ?? 0
    const gross = load.bagsUnloaded ?? 0
    return Math.max(0, gross - wet - other)
  }
  return load.bagsUnloaded ?? 0
}

// Total kg the load was accepted for, after refraction. Falls back to
// the raw accepted weight, then to net weight.
export function loadAcceptedKg(load: MillFloorLoad): number {
  return (
    load.netAfterRefractionKg ??
    load.acceptedWeightKg ??
    load.netWeight ??
    0
  )
}

// Average bag weight in kg. Snapshotted at coupling time on the
// assignment row, but here we derive it from the load's own totals
// for warehouse / picker math. Divides by the post-sondage bag count
// so the per-bag weight reflects what's actually in the warehouse.
export function loadAvgBagKg(load: MillFloorLoad): number {
  const bags = loadTotalBags(load)
  if (bags <= 0) return 0
  return loadAcceptedKg(load) / bags
}

// Bags currently locked into a non-completed usinage. The backend
// annotates each load with all its assignments (including completed
// ones); for "remaining" math we only count those still tying up bags.
export function loadAssignedBagsActive(load: MillFloorLoad): number {
  const a = load.assignments ?? []
  let n = 0
  for (const row of a) {
    if (row.usinage && row.usinage.status !== "COMPLETED") {
      n += row.bagsAssigned ?? 0
    }
  }
  return n
}

// Bags consumed by a completed usinage — these have left the warehouse
// for good and don't count as "remaining" anymore.
export function loadConsumedBags(load: MillFloorLoad): number {
  const a = load.assignments ?? []
  let n = 0
  for (const row of a) {
    if (row.usinage && row.usinage.status === "COMPLETED") {
      n += row.bagsAssigned ?? 0
    }
  }
  return n
}

// What's left to assign: original bags minus everything already on a
// non-COMPLETED usinage AND everything already consumed by a completed
// run. Clamped at 0.
export function loadRemainingBags(load: MillFloorLoad): number {
  return Math.max(
    0,
    loadTotalBags(load) -
      loadAssignedBagsActive(load) -
      loadConsumedBags(load),
  )
}

// Net kg still in the warehouse for this load (remaining bags × avg).
// We don't have a stored "remaining kg" — derive on the fly.
export function loadRemainingKg(load: MillFloorLoad): number {
  return loadRemainingBags(load) * loadAvgBagKg(load)
}

// Cooperative name comes through the Orders -> groupId embed.
export function loadCooperativeName(load: MillFloorLoad): string | null {
  return load.Orders?.groupId?.name ?? null
}

// Per-row bag count to render in the warehouse table, varying with the
// load's current derived status. "Available" shows what's still in the
// warehouse; "assigned" shows what's locked into the active run; "consumed"
// shows what was milled out.
export function rowBagsForStatus(load: MillFloorLoad): number {
  switch (load.derivedStatus) {
    case "available":
      return loadRemainingBags(load)
    case "assigned":
      return loadAssignedBagsActive(load)
    case "consumed":
      return loadConsumedBags(load) || loadTotalBags(load)
  }
}

// Per-row weight for the warehouse table — same shape as `rowBagsForStatus`.
export function rowKgForStatus(load: MillFloorLoad): number {
  return rowBagsForStatus(load) * loadAvgBagKg(load)
}

// Same idea as `rowBagsForStatus`, but driven by the *tab the user is
// looking at* rather than the load's single derived status. A load
// with 300 free + 900 assigned should appear under BOTH tabs and the
// row needs to render the slice that belongs to whichever tab is
// open. The legacy `rowBagsForStatus` only handled the single-status
// case and is kept for the "All" tab's primary-state rendering.
export function rowBagsForTab(
  load: MillFloorLoad,
  tab: "available" | "assigned" | "consumed",
): number {
  switch (tab) {
    case "available":
      return loadRemainingBags(load)
    case "assigned":
      return loadAssignedBagsActive(load)
    case "consumed":
      return loadConsumedBags(load)
  }
}

export function rowKgForTab(
  load: MillFloorLoad,
  tab: "available" | "assigned" | "consumed",
): number {
  return rowBagsForTab(load, tab) * loadAvgBagKg(load)
}

// ---------------------------------------------------------------------
// Queue de Lot equivalents. A QDL is a virtual chargement carved out
// of a previous usinage's QUEUE-kind lot; the warehouse / picker
// surfaces it under its own "Queue de Lot" tab. These helpers mirror
// the LOAD ones (`qdl*Bags`, `qdl*Kg`) so consumers can pick the
// right pair based on the row's source kind.
// ---------------------------------------------------------------------

// Original fabrication bag count.
export function qdlTotalBags(qdl: MillFloorQueueLot): number {
  return qdl.bags ?? 0
}

// Per-bag weight. Authoritative if the backend already computed it on
// the lot row; otherwise derive from netKg / bags.
export function qdlAvgBagKg(qdl: MillFloorQueueLot): number {
  if (qdl.avgBagKg != null && qdl.avgBagKg > 0) return qdl.avgBagKg
  const bags = qdlTotalBags(qdl)
  if (bags <= 0) return 0
  return (qdl.netKg ?? 0) / bags
}

// Bags currently locked into a non-completed re-coupling.
export function qdlAssignedBagsActive(qdl: MillFloorQueueLot): number {
  const a = qdl.assignments ?? []
  let n = 0
  for (const row of a) {
    if (row.usinage && row.usinage.status !== "COMPLETED") {
      n += row.bagsAssigned ?? 0
    }
  }
  return n
}

// Bags already spent on a previously-completed coupling. Authoritative
// from the QDL's own `consumedBags` column — the assignments embed
// only carries non-completed reservations through the warehouse path.
export function qdlConsumedBags(qdl: MillFloorQueueLot): number {
  return qdl.consumedBags ?? 0
}

// What's still in stock and available to be re-coupled.
export function qdlRemainingBags(qdl: MillFloorQueueLot): number {
  return Math.max(
    0,
    qdlTotalBags(qdl) - qdlAssignedBagsActive(qdl) - qdlConsumedBags(qdl),
  )
}

// Net kg still in stock (remaining bags × avg).
export function qdlRemainingKg(qdl: MillFloorQueueLot): number {
  return qdlRemainingBags(qdl) * qdlAvgBagKg(qdl)
}

// Per-row bag count for the QDL warehouse tab, varying with derived
// status (mirrors `rowBagsForStatus`).
export function qdlRowBagsForStatus(qdl: MillFloorQueueLot): number {
  switch (qdl.derivedStatus) {
    case "available":
      return qdlRemainingBags(qdl)
    case "assigned":
      return qdlAssignedBagsActive(qdl)
    case "consumed":
      return qdlConsumedBags(qdl) || qdlTotalBags(qdl)
  }
}

export function qdlRowKgForStatus(qdl: MillFloorQueueLot): number {
  return qdlRowBagsForStatus(qdl) * qdlAvgBagKg(qdl)
}

// QDL equivalents of rowBagsForTab / rowKgForTab — tab-context-aware
// slice rather than derived-status-driven.
export function qdlRowBagsForTab(
  qdl: MillFloorQueueLot,
  tab: "available" | "assigned" | "consumed",
): number {
  switch (tab) {
    case "available":
      return qdlRemainingBags(qdl)
    case "assigned":
      return qdlAssignedBagsActive(qdl)
    case "consumed":
      return qdlConsumedBags(qdl)
  }
}

export function qdlRowKgForTab(
  qdl: MillFloorQueueLot,
  tab: "available" | "assigned" | "consumed",
): number {
  return qdlRowBagsForTab(qdl, tab) * qdlAvgBagKg(qdl)
}

// ---------------------------------------------------------------------
// Usinage-level aggregates.
// ---------------------------------------------------------------------

// Aggregate bag count attached to a usinage (raw input from both
// source streams).
export function usinageInputBags(usinage: MillFloorUsinage): number {
  const a = usinage.assignments ?? []
  const q = usinage.qdlAssignments ?? []
  const loadBags = a.reduce((acc, r) => acc + (r.bagsAssigned ?? 0), 0)
  const qdlBags = q.reduce((acc, r) => acc + (r.bagsAssigned ?? 0), 0)
  return loadBags + qdlBags
}

// Aggregate input kg = sum(bagsAssigned × snapshotted avgBagKg)
// across both source streams. avgBagKg was snapshotted at schedule
// time so this is stable even if the source drifts later.
export function usinageInputKg(usinage: MillFloorUsinage): number {
  const a = usinage.assignments ?? []
  const q = usinage.qdlAssignments ?? []
  const loadKg = a.reduce(
    (acc, r) => acc + (r.bagsAssigned ?? 0) * (r.avgBagKg ?? 0),
    0,
  )
  const qdlKg = q.reduce(
    (acc, r) => acc + (r.bagsAssigned ?? 0) * (r.avgBagKg ?? 0),
    0,
  )
  return loadKg + qdlKg
}

export function usinageOutputKg(usinage: MillFloorUsinage): number {
  const lots = usinage.lots ?? []
  return lots.reduce((acc, l) => acc + (l.netKg ?? 0), 0)
}

export function usinageLoadCount(usinage: MillFloorUsinage): number {
  const a = (usinage.assignments ?? []).length
  const q = (usinage.qdlAssignments ?? []).length
  return a + q
}

// Number of full (≥ target) lots produced by the run.
export function usinageFullLotCount(
  usinage: MillFloorUsinage,
  target: number,
): number {
  return (usinage.lots ?? []).filter((l) => (l.bags ?? 0) >= target).length
}

// Number of lots produced (any size). Used when the run is COMPLETED.
export function usinageActualLots(usinage: MillFloorUsinage): number {
  return (usinage.lots ?? []).length
}

// Sum of bags currently held in lots (including queue lots).
export function usinageLotBagsTotal(usinage: MillFloorUsinage): number {
  return (usinage.lots ?? []).reduce((acc, l) => acc + (l.bags ?? 0), 0)
}

// Residual kg = input − output, clamped to 0.
export function usinageResidualKg(usinage: MillFloorUsinage): number {
  return Math.max(0, usinageInputKg(usinage) - usinageOutputKg(usinage))
}
